import { useEffect, useRef, useState } from "react";

export const AWAY_REFRESH_THRESHOLD_MS = (8 * 60 + 15) * 60 * 1000;
const DEFAULT_POLL_INTERVAL_MS = 60_000;

export function isAwayLongEnough(
  awaySince: number | null,
  now: number,
  thresholdMs: number = AWAY_REFRESH_THRESHOLD_MS,
): boolean {
  if (awaySince === null) return false;
  return now - awaySince >= thresholdMs;
}

interface AwayRefreshOptions {
  enabled: boolean;
  thresholdMs?: number;
  pollIntervalMs?: number;
}

interface AwayRefreshState {
  open: boolean;
  dismiss: () => void;
}

export function useAwayRefreshPrompt({
  enabled,
  thresholdMs = AWAY_REFRESH_THRESHOLD_MS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: AwayRefreshOptions): AwayRefreshState {
  const [open, setOpen] = useState(false);
  const awaySince = useRef<number | null>(null);
  // Baseline set inside the effect: Date.now() during render is impure and
  // blocks React Compiler memoization for the whole hook.
  const lastTick = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      // Disabled means non-desktop or signed out. Stop the clock and clear any
      // pending prompt so away time never accrues while logged out and a stale
      // prompt can't survive a sign-out/sign-in cycle.
      awaySince.current = null;
      lastTick.current = null;
      setOpen(false);
      return;
    }

    const markAway = () => {
      if (awaySince.current === null) awaySince.current = Date.now();
    };
    const evaluateReturn = () => {
      if (isAwayLongEnough(awaySince.current, Date.now(), thresholdMs)) {
        setOpen(true);
      }
      awaySince.current = null;
    };
    const onVisibility = () => {
      if (document.hidden) markAway();
      else evaluateReturn();
    };

    // Mounting while already hidden or unfocused (the desktop window was
    // backgrounded before this ran) must still start the away clock, or a
    // return past the away threshold would be measured against a null baseline and
    // never prompt.
    if (document.hidden || !document.hasFocus()) markAway();

    // Belt and braces for machine sleep and lock: timers do not advance while
    // the host is suspended, so a tick gap larger than the threshold means we
    // were effectively away even if no visibility or focus event fired on wake.
    lastTick.current = Date.now();
    const interval = window.setInterval(() => {
      const now = Date.now();
      if (now - (lastTick.current ?? now) >= thresholdMs) setOpen(true);
      lastTick.current = now;
    }, pollIntervalMs);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", markAway);
    window.addEventListener("focus", evaluateReturn);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", markAway);
      window.removeEventListener("focus", evaluateReturn);
      window.clearInterval(interval);
    };
  }, [enabled, thresholdMs, pollIntervalMs]);

  const dismiss = () => {
    setOpen(false);
    awaySince.current = null;
    lastTick.current = Date.now();
  };

  // Gate the reported state on enabled so a prompt can never render while
  // disabled (signed out / non-desktop), even if a late interval callback or a
  // stale open value slips through before the effect's reset runs.
  return { open: enabled && open, dismiss };
}
